import * as React from 'react';
import cn from 'classnames';

import { PopupPosition } from 'shared/entities/components/Popup';
import {
  SelectorKey,
  SelectorOptions,
  SelectorType,
  SelectorTopItem,
  SelectorSize
} from 'shared/newEntities/components/Selector';
import {
  TypographySize,
  TypographyWeight,
  TypographyColor
} from 'shared/newEntities/components/Typography';
import { VIRTUAL_LIST_VIRTUOSO_MIN_COUNT } from 'shared/newEntities/components/VirtualizedList';
import { LoadingStage } from 'shared/entities/meta';
import LoaderSpinner from 'shared/newComponents/animations/LoaderSpinner';
import {
  LoaderSpinnerColor,
  LoaderSpinnerSize
} from 'shared/newEntities/components/LoaderSpinner';
import { TranslationString } from 'shared/entities/localization';

import SelectorOption from './SelectorOption';
import TopItem from './TopItem';
import SelectAllItem from './SelectAllItem';
import OptionsList from './OptionsList';
import EmptyContent from './EmptyContent';
import HelperText from './HelperText';

import './Content.modules.scss';

type CommonProps = {
  options: SelectorOptions;
  onClose: () => void;
  onOptionClick: (id: SelectorKey) => void;
  loadingStage?: LoadingStage;
  topItem?: SelectorTopItem;
  topNode?: React.ReactNode | ((onClose: () => void) => React.ReactNode);
  onRightIconClick?: (key: SelectorKey) => void;
  largeOption?: boolean;
  selectorSize?: SelectorSize;
  fontSize?: TypographySize;
  fontWeight?: TypographyWeight;
  fontColor?: TypographyColor;
  smallDescription?: boolean;
  hoverReferencePosition?: PopupPosition;
  helperText?: TranslationString | null;
  emptyText?: TranslationString;
  listResetKey?: string | number;
  withStub?: boolean;
  stablePopupSize?: boolean;
};

type Props = CommonProps &
  (
    | {
        type: SelectorType.single;
        value: SelectorKey | null;
        withRemoving?: boolean;
      }
    | {
        type: SelectorType.multi;
        value: SelectorKey[];
        selectedAllOptions?: {
          shouldClear: boolean;
          handler: () => void;
        };
      }
  );

const Content: React.FC<Props> = (props) => {
  const {
    onClose,
    onOptionClick,
    loadingStage,
    options,
    topItem,
    topNode,
    helperText,
    emptyText,
    largeOption,
    selectorSize,
    fontSize,
    fontWeight,
    fontColor,
    smallDescription,
    hoverReferencePosition,
    onRightIconClick,
    type,
    value,
    listResetKey,
    withStub,
    stablePopupSize
  } = props;

  const selectedAllOptions =
    type === SelectorType.multi ? props.selectedAllOptions : undefined;

  const optionSpread =
    type === SelectorType.single
      ? {
          type,
          value,
          withRemoving: props.withRemoving,
          largeOption,
          selectorSize,
          fontSize,
          fontWeight,
          fontColor,
          smallDescription,
          hoverReferencePosition,
          onRightIconClick
        }
      : {
          type,
          value,
          largeOption,
          selectorSize,
          fontSize,
          fontWeight,
          fontColor,
          smallDescription,
          hoverReferencePosition,
          onRightIconClick
        };

  const handleOptionClick = React.useCallback(
    (id: SelectorKey) => {
      onOptionClick(id);

      if (type === SelectorType.multi) {
        return;
      }

      onClose();
    },
    [type, onClose, onOptionClick]
  );

  const hasOptions = options.keys.length > 0;
  const isLoading = loadingStage === LoadingStage.LOADING;
  const withVirtuoso = options.keys.length > VIRTUAL_LIST_VIRTUOSO_MIN_COUNT;
  const defaultEmptyText: TranslationString = (t) =>
    t('Selector.emptyList', { ns: 'newComponents' });

  const showEmpty = !hasOptions && !helperText;
  const emptyTextContent = emptyText ?? defaultEmptyText;

  const renderOption = React.useCallback(
    (index: number) => {
      const optionKey = options.keys[index];
      const option = options.entities[optionKey];

      if (!option) {
        return null;
      }

      return (
        <SelectorOption
          key={optionKey}
          {...option}
          {...optionSpread}
          onClick={handleOptionClick}
          extraComponent={option.extraComponent}
        />
      );
    },
    [options.keys, options.entities, optionSpread, handleOptionClick]
  );

  return (
    <>
      {topItem && (
        <TopItem {...topItem} onClose={onClose} selectorSize={selectorSize} />
      )}
      {type === SelectorType.multi && selectedAllOptions && (
        <SelectAllItem
          shouldClear={selectedAllOptions.shouldClear}
          handler={selectedAllOptions.handler}
          selectorSize={selectorSize}
        />
      )}
      {typeof topNode === 'function' ? topNode(onClose) : topNode}
      {helperText && <HelperText text={helperText} withContent={hasOptions} />}
      <div styleName="content-container">
        <div
          styleName={cn(
            'content',
            stablePopupSize && 'content_stable-height',
            isLoading && 'content_loading'
          )}
        >
          {isLoading && (
            <LoaderSpinner
              size={LoaderSpinnerSize.sm}
              color={LoaderSpinnerColor.blue}
            />
          )}
          {!isLoading && (
            <>
              {showEmpty && (
                <EmptyContent text={emptyTextContent} withStub={withStub} />
              )}
              {hasOptions && (
                <OptionsList
                  optionKeys={options.keys}
                  withVirtuoso={withVirtuoso}
                  listResetKey={listResetKey}
                  itemContent={renderOption}
                />
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default React.memo(Content);
